import React, { useCallback, useMemo, useEffect } from "react";
import { Image, Form, Input } from "semantic-ui-react";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./InstitutionForm.form";
import { InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";

const institutionController = new InstitutionApi();

export function InstitutionForm(props) {
  const { close, onReload, user, institution, plans } = props;
  const { accessToken } = useAuth();

  // Generar opciones para el dropdown de planes
  const planOptions = useMemo(() => {
    if (!plans) return [];
    return plans.map(plan => ({
      key: plan._id,
      value: plan._id,
      text: plan.title,
      numberStudents: plan.studentsNumber,
      numberTeachers: plan.teacherNumber,
    }));
  }, [plans]);

  const formik = useFormik({
    initialValues: initialValues(user, institution, !institution),
    validationSchema: validationSchema(institution),
    validateOnChange: false,
    onSubmit: async values => {
      try {
        if (!institution) {
          await institutionController.createInstitution(accessToken, values);
        } else {
          if (formik.values.fileAvatar && institution.avatar) {
            await institutionController.deleteAvatar(accessToken, institution.avatar);
          }
          await institutionController.updateInstitution(accessToken, institution._id, values);
        }
        onReload();
        close();
      } catch (error) {
        console.error(error);
      }
    }
  });

  // Dropzone para avatar
  const onDrop = useCallback(files => {
    const file = files[0];
    if (file && ["image/jpeg","image/png"].includes(file.type)) {
      formik.setFieldValue("avatar", URL.createObjectURL(file));
      formik.setFieldValue("fileAvatar", file);
    }
  }, [formik]);
  const { getRootProps, getInputProps } = useDropzone({ accept: { "image/jpeg": [".jpeg",".jpg"], "image/png": [".png"] }, onDrop, multiple: false });

  // Al seleccionar plan en creación, actualizamos licencias
  useEffect(() => {
    if (!institution && formik.values.planEducaId) {
      const sel = planOptions.find(o => o.value === formik.values.planEducaId);
      if (sel) {
        formik.setFieldValue("studentsnum", sel.numberStudents ?? "");
        formik.setFieldValue("teachersnum", sel.numberTeachers ?? "");
      }
    }
  }, [formik.values.planEducaId, planOptions, institution]);

  const getAvatar = () => {
    if (formik.values.fileAvatar) return formik.values.avatar;
    return formik.values.avatar || image.noAvatar;
  };

  return (
    <Form onSubmit={formik.handleSubmit} className="institution-form">
      {/* Avatar */}
      <Form.Field>
        <label>Imagen del centro</label>
        <div {...getRootProps()} className="institution-form__avatar">
          <input {...getInputProps()} />
          <Image avatar size="small" src={getAvatar()} />
        </div>
      </Form.Field>

      {/* Partner ID */}
      <Form.Field>
        <label>Partner ID</label>
        <span>{formik.values.partnerId}</span>
      </Form.Field>

      {/* Nombre e info básica */}
      <Form.Field control={Form.Input} label="Nombre de la institución" name="institutionnam" placeholder="Ej. Colegio..." onChange={formik.handleChange} value={formik.values.institutionnam} error={formik.errors.institutionnam} />
      <Form.Field control={Form.Input} label="Dirección" name="address" placeholder="Ej. Calle..." onChange={formik.handleChange} value={formik.values.address} error={formik.errors.address} />

      <Form.Group widths="equal">
        <Form.Field control={Form.Input} label="Ciudad" name="city" placeholder="Ej. Madrid" onChange={formik.handleChange} value={formik.values.city} error={formik.errors.city} />
        <Form.Field control={Form.Input} label="País" name="country" placeholder="Ej. España" onChange={formik.handleChange} value={formik.values.country} error={formik.errors.country} />
      </Form.Group>

      <Form.Field control={Form.Input} label="Sitio Web" name="website" placeholder="https://..." onChange={formik.handleChange} value={formik.values.website} error={formik.errors.website} />

      <Form.Group widths="equal">
        <Form.Field control={Form.Input} label="Teléfono" name="phone" placeholder="600123123" onChange={formik.handleChange} value={formik.values.phone} error={formik.errors.phone} />

        {/* Plan contratado: editable solo en creación */}
        {institution ? (
          <Form.Field>
            <label>Plan contratado</label>
            <Input readOnly value={institution.planEduca?.title || "-"} />
          </Form.Field>
        ) : (
          <Form.Field control={Form.Dropdown} label="Plan contratado" placeholder="Selecciona un plan" options={planOptions} selection onChange={(_,d) => formik.setFieldValue("planEducaId",d.value)} value={formik.values.planEducaId} error={formik.errors.planEducaId} />
        )}
      </Form.Group>

      {/* Contacto */}
      <Form.Group widths="equal">
        <Form.Field control={Form.Input} label="Nombre Contacto" name="firstname" placeholder="Ej. Juan" onChange={formik.handleChange} value={formik.values.firstname} error={formik.errors.firstname} />
        <Form.Field control={Form.Input} label="Apellidos" name="lastname" placeholder="Ej. Pérez" onChange={formik.handleChange} value={formik.values.lastname} error={formik.errors.lastname} />
      </Form.Group>

      {/* Licencias: solo lectura */}
      <Form.Group widths="equal">
        <Form.Field control={Form.Input} label="Licencias estudiantes" name="studentsnum" value={formik.values.studentsnum} disabled error={formik.errors.studentsnum} />
        <Form.Field control={Form.Input} label="Licencias profesores" name="teachersnum" value={formik.values.teachersnum} disabled error={formik.errors.teachersnum} />
      </Form.Group>

      {/* Email & Password */}
      <Form.Field control={Form.Input} label="Correo electrónico" name="email" placeholder="institucion@..." onChange={formik.handleChange} value={formik.values.email} error={formik.errors.email} autoComplete="off" />
      <Form.Field control={Form.Input} type="password" label="Contraseña" name="password" placeholder={institution ? "Dejar en blanco..." : "Contraseña"} onChange={formik.handleChange} value={formik.values.password} error={formik.errors.password} autoComplete="new-password" />

      <Form.Button primary fluid loading={formik.isSubmitting} type="submit">
        {institution ? "Actualizar institución" : "Crear institución"}
      </Form.Button>
    </Form>
  );
}
